import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LocomotiveScroll from 'locomotive-scroll';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { CartProvider } from './context/CartContext';
import Home from './pages/Home';
import ProductPage from './pages/ProductPage';
import ScooterBuilder from './pages/ScooterBuilder';
import Products from './pages/Products';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import OrderSuccessPage from './pages/OrderSuccessPage';
import MerchantFeed from './pages/MerchantFeed';
import Contact from './pages/Contact';
import Shipping from './pages/Shipping';
import About from './pages/About';
import Returns from './pages/Returns';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Module-level ref so ScrollToTop can access the Locomotive instance
let locomotiveInstance: InstanceType<typeof LocomotiveScroll> | null = null;

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (locomotiveInstance) {
      try {
        locomotiveInstance.scrollTo(0, { duration: 0, disableLerp: true });
      } catch {
        // fallback
      }
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

// Component to handle redirects from old /touksid?param=value URLs to new /toukeratas/* URLs
function RedirectOldTouksidUrls() {
  const [searchParams] = useSearchParams();

  const classParam = searchParams.get('class');
  const sortByParam = searchParams.get('sortBy');
  const categoryParam = searchParams.get('category');
  const brandParam = searchParams.get('brand');
  const searchParam = searchParams.get('search');

  let redirectPath = '/toukeratas';

  if (classParam === 'complete-scooters') {
    redirectPath = '/toukeratas';
  } else if (classParam === 'decks') {
    redirectPath = '/toukeratas/tald';
  } else if (classParam === 'bars') {
    redirectPath = '/toukeratas/lenks';
  } else if (sortByParam === 'discount') {
    redirectPath = '/toukeratas/soodus-tooted';
  } else if (sortByParam === 'new') {
    redirectPath = '/toukeratas/uued-tooted';
  } else if (categoryParam === 'Kesktase') {
    redirectPath = '/toukeratas/kesktase-toukerattad';
  } else if (categoryParam === 'Pro tase') {
    redirectPath = '/toukeratas/pro-tase-toukerattad';
  } else if (categoryParam === 'Street') {
    redirectPath = '/toukeratas/street-toukerattad';
  } else if (categoryParam === 'Park') {
    redirectPath = '/toukeratas/pargi-toukerattad';
  } else if (brandParam) {
    redirectPath = `/toukeratas/brandid/${brandParam}`;
  } else if (searchParam) {
    redirectPath = `/toukeratas?search=${encodeURIComponent(searchParam)}`;
  }

  return <Navigate to={redirectPath} replace />;
}

// ─── AppContent: inside Router, can use useLocation ──────────────────────────
function AppContent() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const scrollRef = useRef(null);

  const isAdmin = location.pathname.startsWith('/admin');

  const parts = ['Tallad', 'Lenksud', 'Rattad', 'Klambrid', 'Kahvlid', 'Muud'];

  // Locomotive scroll — only for the normal shop layout
  useEffect(() => {
    if (isAdmin) return;
    const el = document.querySelector('[data-scroll-container]') as HTMLElement | null;
    if (!el) return;
    const scroll = new LocomotiveScroll({ el, smooth: true, lerp: 0.06 });
    locomotiveInstance = scroll;
    return () => {
      if (scroll) scroll.destroy();
      locomotiveInstance = null;
    };
  }, [isAdmin]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Admin routes (no Header / Footer / Locomotive) ──
  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  // ── Normal shop layout ──
  return (
    <div className="min-h-screen flex flex-col" data-scroll-container ref={scrollRef}>
      <div className="geometric-bg"></div>
      <div className="scooter-pattern"></div>
      <div className="dynamic-lines"></div>
      <div className="scooter-tracks"></div>

      <Header
        isScrolled={isScrolled}
        showMegaMenu={showMegaMenu}
        setShowMegaMenu={setShowMegaMenu}
        parts={parts}
      />
      <div className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tooted/:id" element={<ProductPage />} />
          <Route path="/ehita-touks" element={<ScooterBuilder />} />

          {/* SEO-optimized routes */}
          <Route path="/toukeratas" element={<Products />} />
          <Route path="/toukeratas/complete-toukerattad" element={<Navigate to="/toukeratas" replace />} />
          <Route path="/toukeratas/tald" element={<Products />} />
          <Route path="/toukeratas/lenks" element={<Products />} />
          <Route path="/toukeratas/soodus-tooted" element={<Products />} />
          <Route path="/toukeratas/uued-tooted" element={<Products />} />
          <Route path="/toukeratas/kesktase-toukerattad" element={<Products />} />
          <Route path="/toukeratas/pro-tase-toukerattad" element={<Products />} />
          <Route path="/toukeratas/street-toukerattad" element={<Products />} />
          <Route path="/toukeratas/pargi-toukerattad" element={<Products />} />
          <Route path="/toukeratas/rattad" element={<Products />} />
          <Route path="/toukeratas/kahvlid" element={<Products />} />
          <Route path="/toukeratas/klambrid" element={<Products />} />
          <Route path="/toukeratas/lenksuotsad" element={<Products />} />
          <Route path="/toukeratas/peegid" element={<Products />} />
          <Route path="/toukeratas/kaepidemed" element={<Products />} />
          <Route path="/toukeratas/gripteibid" element={<Products />} />
          <Route path="/toukeratas/toukeratta-varuosad" element={<Products />} />
          <Route path="/toukeratas/laste-toukeratas" element={<Products />} />
          <Route path="/toukeratas/brandid" element={<Products />} />
          <Route path="/toukeratas/brandid/:brand" element={<Products />} />

          {/* Redirects from old URLs */}
          <Route path="/touksid" element={<Navigate to="/toukeratas" replace />} />
          <Route path="/jupid/*" element={<Navigate to="/toukeratas" replace />} />
          <Route path="/touksid/*" element={<RedirectOldTouksidUrls />} />

          <Route path="/blogi" element={<Blog />} />
          <Route path="/blogi/:slug" element={<BlogPost />} />
          <Route path="/kas-teadsid" element={<Navigate to="/blogi" replace />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/kohaletoimetamine" element={<Shipping />} />
          <Route path="/meist" element={<About />} />
          <Route path="/garantii-ja-tagastamine" element={<Returns />} />
          <Route path="/muugitingimused" element={<Terms />} />
          <Route path="/privaatsustingimused" element={<Privacy />} />
          <Route path="/ostukorv" element={<Cart />} />
          <Route path="/kassa" element={<Checkout />} />
          <Route path="/tellimus-esitatud" element={<OrderSuccessPage />} />
          <Route path="/merchant-feed" element={<MerchantFeed />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <CartProvider>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </HelmetProvider>
    </CartProvider>
  );
}

export default App;
